import Vue from 'vue'
import promise from 'promiscuous'
import apiService, { RestError } from 'services/api-service'
import { arrayFind } from 'util/ts-utils'
import { callTurnToConfigFn } from 'util/turnto-utils'
import { CDN_URL } from 'services/http-client'
import { hostsConfig, turnToConfig } from 'init/config'
import globalConfig from 'init/global.config'
import globalStore from 'init/global-store'
import gridLayoutDataMixin from 'layouts/grid/grid-layout-data-mixin'
import { loadJs } from 'util/browser-utils'
import logger from 'util/logger'
import { getMediaList } from 'services/media-service'
import reviewsSummaryConfig from './reviews-summary.config'
import reviewsSummaryStore from 'store/reviews-summary-store'
import submissionLoader from 'widgets/submission/submission-loader'
import submissionStore, { LaunchData, Source, SubmissionPage } from 'store/submission-store'
import { ssoCreateReview, ssoGetLoggedInDataForReview } from 'services/sso-service'
import ActionableRatingStars from 'components/rating/actionable-rating-stars'
import GridLayout from 'layouts/grid/grid-layout'
import SummaryDimRating from 'components/reviews-summary/summary-dim-rating'
import SummaryDimRange from 'components/reviews-summary/summary-dim-range'
import SummaryDimSelect from 'components/reviews-summary/summary-dim-select'
import SummaryDimSelectImage from 'components/reviews-summary/summary-dim-select-image'
import RatingsBreakdown from 'components/reviews-summary/ratings-breakdown'
import RatingStars from 'components/rating/rating-stars'
import UserToolbar from 'components/user-toolbar/user-toolbar'

import withRender from './reviews-summary.html'
import './reviews-summary.scss'

const JSON_LOADER_ID = 'tt-agg-json-ld'

// Needing to explicitly include here because for some reason Webpack is not using the Promise
// provided plugin need for a particular case here.  When the gallery is included as a widget
// on the same page where the gallery is included in the reviews summary, it is using a Promise
// to get the already included gallery component.  In other words, it does not need to be
// dynamically loaded since it is already included in the gallery widget.  This causes a Promise
// to be used in this component.  This is fine for most browsers that have a native Promise, but
// not for IE11 and such.  Part of TT-11452 fix.
// @ts-ignore - for noUnusedLocals
// tslint:disable-next-line:variable-name
const Promise = promise

export default withRender(Vue.extend({
  data() {
    return {
      customDimensions: [],
      catItem: null,
      dataIsLoaded: false,
      isGalleryHidden: false, // only to be used in hasHiddenForDisplay computed propery
      globalStore,
      rating: 0.0,
      ratingFormatted: '',
      ratingsBreakdown: [],
      recommendedPercentageFormatted: '',
      reviewsCount: 0,
      reviewsSummaryConfig,
      submissionStore,
      showDetailsClicked: false
    }
  },
  components: {
    'tt-actionable-rating-stars': ActionableRatingStars,
    'tt-ratings-breakdown': RatingsBreakdown,
    'tt-rating-stars': RatingStars,
    'tt-grid-layout': GridLayout,
    'tt-summary-custom-rating': SummaryDimRating,
    'tt-summary-custom-range': SummaryDimRange,
    'tt-summary-custom-select': SummaryDimSelect,
    'tt-summary-custom-select-image': SummaryDimSelectImage,
    // tslint:disable-next-line
    'tt-gallery':  () => import(/* webpackChunkName: 'gallery' */ 'components/gallery/gallery'),
    // tslint:disable-next-line
    'tt-spectrum':  () => import(/* webpackChunkName: 'spectrum' */ 'components/spectrum/spectrum'),
    'tt-user-toolbar': UserToolbar
  },
  mixins: [gridLayoutDataMixin],
  computed: {
    aggregateAttributes() {
      if (this.aggregateMetaOn && this.reviewsCount > 0) {
        return {
          itemprop: 'aggregateRating',
          itemscope: true,
          itemtype: 'http://schema.org/AggregateRating'
        }
      }
      return {}
    },
    aggregateMetaOn() {
      return this.reviewsSummaryConfig.aggregateMetaOn
    },
    currentSku(): string {
      return this.globalStore.sku
    },
    displayShowDetails() {
      return !this.showDetailsClicked && ~this.allItemsInLayout.indexOf('showDetails') &&
        this.hasHiddenForDisplay
    },
    galleryConfig() {
      return reviewsSummaryConfig.childWidgets['gallery']
    },
    hasGallery() {
      return ~this.allItemsInLayout.indexOf('gallery')
    },
    hasSpectrum() {
      return ~this.allItemsInLayout.indexOf('spectrum')
    },
    // this determines whether any of the hidden items (after "show details") actually have data
    // for display. For example, customDim-5 should not be included if there are only 4 custom dims.
    // Used to determine whether or not "show details" should even display.
    // Just need to know that at least one of them does.
    hasHiddenForDisplay() {
      return arrayFind(this.slotNamesAfterShowDetails, (slotName) => {
        if (slotName === 'recommend') {
          return this.recommendedPercentageFormatted
        } else if (slotName.indexOf('customDim') === 0) {
          const dimNum = parseInt(slotName.split('-')[1], 10)
          return this.customDimensions.length >= dimNum
        } else if (slotName === 'spectrum') {
          return this.reviewsCount > 1 // TODOL is this correct?
        } else if (slotName !== 'gallery') {
          // all other items should have data.  Returning true here to avoid having to make call
          // to see if gallery has media or not
          return true
        } else if (slotName === 'gallery') {
          if (this.isGalleryHidden) {
            return true
          }
          // checking to see if there are any images in the gallery to be displayed
          getMediaList([this.currentSku], [], 0, this.galleryConfig)
            .then(data => this.isGalleryHidden = data.media.length > 0)
          // return false now that check has been kicked-off.  isGalleryHidden will change to
          // true if there actually are images.
          return false
        }
      }) ? true : false
    },
    isLaunchingWriteReview() {
      return this.submissionStore.isLaunchingWriteReview
    },
    spectrumConfig() {
      return this.reviewsSummaryConfig.childWidgets['spectrum']
    }
  },
  methods: {
    fetchData() {
      if (this.currentSku) {
        apiService.reviewsSummary({
          sku: this.currentSku
        }).then((data) => {
          if (!data) { // invalid sku most likely
            return
          }
          this.catItem = data.catItem
          this.reviewsCount = data.reviewsCount
          this.recommendedPercentageFormatted = data.recommendedPercentageFormatted
          this.rating = data.rating
          this.ratingFormatted = data.ratingFormatted
          this.ratingsBreakdown = data.ratingsBreakdown
          this.customDimensions = data.customDimensions
          this.dataIsLoaded = !data.hideDisplay
          this.$nextTick(() => { callTurnToConfigFn('reviewsSummary', 'onFinish') })
        }).catch((e: RestError) => {
          if (e.errorCode === 'turnto-offline') {
            logger.info('TurnTo is in offline mode - Reviews Summary not loaded', e)
          } else {
            logger.error('Error loading data for the Reviews Summary widget', e)
          }
        })
      }
    },
    isSlotHidden(slotName) {
      return !this.showDetailsClicked && ~this.slotNamesAfterShowDetails.indexOf(slotName)
    },
    loadJsonLd() {
      if (this.globalStore.sku && !this.aggregateMetaOn
        && reviewsSummaryConfig.aggregateJsonLdMetaOn) {
        loadJs(
          `${CDN_URL}/sitedata/${hostsConfig.siteKey}/${this.globalStore.sku}` +
            `/d/jsonld/${turnToConfig.locale}/${reviewsSummaryStore.schemaOrgEntityType}`,
          JSON_LOADER_ID,
          true,
          false
        ).then(() => document.getElementById(JSON_LOADER_ID).remove())
          .catch(e => logger.error('Error loading json-ld', e))
      }
    },
    onShowDetailsClick() {
      this.showDetailsClicked = true
    },
    writeReview(rating) {
      // if sso, this will be set back to false when ssoRegDone or loggedInDataFnDone is called
      submissionStore.isLaunchingWriteReview = true
      if (globalConfig.ssoEnabled) {
        if (globalConfig.isPurchaseReq()) {
          ssoCreateReview(this.currentSku, Source.INLINE, null, rating)
        } else {
          ssoGetLoggedInDataForReview(this.currentSku, Source.INLINE, rating)
        }
        return
      }
      const launchData = {
        source: Source.INLINE,
        startAction: 'review',
        // need to check since it could be a MouseEvent from the button click
        starSelected: typeof rating === 'number' ? rating : undefined
      } as LaunchData
      const page = { page: 'review-form', props: {} } as SubmissionPage
      const elem = this.$el.querySelector('.tt-c-reviews-summary__write-review')
      submissionStore.startRevFlow(this.currentSku, hostsConfig.siteKey, page, launchData, elem)
      submissionLoader.load()
      submissionStore.isLaunchingWriteReview = false
    }
  },
  watch: {
    //  whenever the currentSku changes, we need to reload the summary data
    currentSku() {
      this.fetchData()
      // TODO: believe the returning script will need to overwrite any existing json-ld, but
      // not sure that works or not
      this.loadJsonLd()
    },
    currentLayout(newLayout, oldLayout) {
      // something is causing this to be called when the layout hasn't changed
      // so checking just in case
      if (newLayout !== oldLayout) {
        this.showDetailsClicked = false
      }
    }
  },
  created() {
    // set the layouts configuration.  This is a method in the gridLayoutDataMixin
    this.setLayoutsConfig(reviewsSummaryConfig.breakpoints)
    this.fetchData()
    this.loadJsonLd()
  }
}))



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/widgets/reviews-summary/reviews-summary.ts